import { Inject, Injectable, Optional } from '@angular/core';
import {
  HttpClient, HttpHeaders, HttpParams
} from '@angular/common/http';

import { GasNetworkDamageModel } from '../model/gasNetworkDamageModel';
import { GasNetworkModel } from '../model/gasNetworkModel';
import { GasNetworkTariffModel } from '../model/gasNetworkTariffModel';
import { GasStationModel } from '../model/gasStationModel';

import { BASE_PATH } from '../variables';
import { Configuration } from '../configuration';
import { ResponseModel } from '@core/api';
import { Observable } from 'rxjs/internal/Observable';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';


@Injectable()
export class GasNetworksService {

  protected basePath = 'https://localhost/v1';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  private networkAutoCompleteSubject = new BehaviorSubject<GasNetworkModel[]>([]);
  public networkAutoComplete = this.networkAutoCompleteSubject.asObservable();

  private networkAutoCompleteLoadingSubject = new BehaviorSubject<boolean>(false);
  public networkAutoCompleteLoading = this.networkAutoCompleteLoadingSubject.asObservable();

  constructor(protected httpClient: HttpClient, @Optional() @Inject(BASE_PATH) basePath: string, @Optional() configuration: Configuration) {
    if (basePath) {
      this.basePath = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.basePath = basePath || configuration.basePath || this.basePath;
    }
  }

  /**
   * @param consumes string[] mime-types
   * @return true: consumes contains 'multipart/form-data', false: otherwise
   */
  private canConsumeForm(consumes: string[]): boolean {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }


  /**
   *
   * Returns a lsit of gas stations network
   */
  public gasNetworkGet(body?: HttpParams): Observable<ResponseModel<GasNetworkModel[]>> {
    const options = body ? {
      params: body
    } : {};

    return this.httpClient.get<ResponseModel<GasNetworkModel[]>>(`${this.basePath}/gas-network`, options);
  }
  /**
   *
   * Returns a list of gas stations network trailer damages pricelist
   * @param id Network identifier
   */
  public gasNetworkIdDamageGet(id: number): Observable<ResponseModel<GasNetworkDamageModel[]>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling gasNetworkIdDamageGet.');
    }

    return this.httpClient.get<ResponseModel<GasNetworkDamageModel[]>>(`${this.basePath}/gas-network/${encodeURIComponent(String(id))}/damage`);
  }

  /**
   *
   * Returns a most suitable gas stations network tariffs
   * @param body
   * @param id Network identifier
   */
  public gasNetworkIdTariffBestPost(body: GasStationModel, id: number): Observable<ResponseModel<GasNetworkTariffModel>> {

    if (body === null || body === undefined) {
      throw new Error('Required parameter body was null or undefined when calling gasNetworkIdTariffBestPost.');
    }

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling gasNetworkIdTariffBestPost.');
    }

    return this.httpClient.post<ResponseModel<GasNetworkTariffModel>>(`${this.basePath}/gas-network/${encodeURIComponent(String(id))}/tariff/best`,
      body
    );
  }

  /**
   *
   * Returns a list of gas stations network tariffs
   * @param id Network identifier
   */
  public gasNetworkIdTariffGet (id: number): Observable<ResponseModel<GasNetworkTariffModel[]>> {

    if (id === null || id === undefined) {
      throw new Error('Required parameter id was null or undefined when calling gasNetworkIdTariffGet.');
    }

    return this.httpClient.get<ResponseModel<GasNetworkTariffModel[]>>(`${this.basePath}/gas-network/${encodeURIComponent(String(id))}/tariff`);
  }

  public gasNetworkIdGet(id: number, body: HttpParams = null): Observable<ResponseModel<GasStationModel>> {
    return this.httpClient.get<ResponseModel<GasStationModel>>(`${ this.basePath }/gas-network/${ encodeURIComponent(String(id)) }`,
        {
          params: body
        }
    );
  }

  getNetwork(htmlParam: HttpParams) {
    return this.gasNetworkGet(htmlParam);
  }

  getStationById(id: number) {
    return this.gasNetworkIdGet(id);
  }

  registerNetworkAutoComplete(nativeElement) {
    fromEvent(nativeElement, 'keydown')
        .subscribe(() => {
          this.networkAutoCompleteLoadingSubject.next(true);
        });

    fromEvent(nativeElement, 'keyup')
        .pipe(
            debounceTime(350),
            distinctUntilChanged(),
            switchMap(() => {
              this.networkAutoCompleteLoadingSubject.next(true);

              return this.gasNetworkGet(
                  new HttpParams()
                      .set('q', nativeElement.value)
              );
            })
        ).subscribe(res => {
      this.networkAutoCompleteLoadingSubject.next(false);
      this.networkAutoCompleteSubject.next(res.data);
    });
  }
}
